<template>
    <ModalWrapper
        v-model="dialog"
        :width="newSupplierMode ? null : 1400"
        scrollable
        fullscreen
        class="add-offline-service"
        :content-class="{'position-relative  overflow-hidden': true}"
        :retain-focus="false"
    >
        <template v-slot:activator="{props}">
            <slot name="activator">
                <v-btn
                    v-if="!serviceToModify"
                    variant="outlined"
                    class="add-offline-service__btn mb-2 text-black"
                    color="grey-darken-1"
                    block
                    data-cy="add-offline-service-button"
                    :size="$breakpoint.smAndDown ? 'small' : 'default'"
                    v-bind="props"
                    v-text="$t('add_offline_service')"
                />
                <a v-else class="mb-3 cursor-pointer text-black modify-link" v-bind="props">
                    {{ $t('manual_correction') }}
                </a>
            </slot>
        </template>
        <template v-slot:title>
            <div class="d-flex full-width align-center">
                <v-btn
                    v-if="newSupplierMode"
                    size="small"
                    elevation="0"
                    class="me-3"
                    variant="text"
                    color="black"
                    @click="newSupplierMode = false"
                >
                    <v-icon> mdi-arrow-left</v-icon>
                </v-btn>
                {{
                    newSupplierMode
                        ? $t('add_supplier')
                        : serviceToModify
                          ? $t('manual_correction')
                          : $t('add_offline_service')
                }}
            </div>
        </template>
        <template v-slot:content>
            <create-supplier-form v-if="newSupplierMode" @save="saveSupplier" />
            <template v-else>
                <v-form ref="form" v-model="valid">
                    <v-row>
                        <v-col cols="12" :md="2">
                            <v-select
                                v-model="type"
                                variant="outlined"
                                density="compact"
                                :disabled="!!serviceToModify"
                                item-title="name"
                                item-value="value"
                                :label="`${$t('type')}`"
                                hide-details
                                :items="types"
                            />
                        </v-col>
                        <v-col cols="12" :md="2">
                            <v-select
                                v-model="status"
                                variant="outlined"
                                density="compact"
                                :label="`${$t('status')}`"
                                hide-details
                                :items="statuses"
                            />
                        </v-col>
                        <v-col cols="12" :md="3">
                            <v-text-field
                                v-model="name"
                                :label="nameLabel"
                                :rules="[v => !!v || `${$t('name')} ${$t('validation.required')}`]"
                                :append-inner-icon="
                                    type === 'ACCOMMODATION'
                                        ? product.hotelId
                                            ? 'mdi-lock-off'
                                            : 'mdi-office-building-plus-outline'
                                        : ''
                                "
                                density="compact"
                                variant="outlined"
                                hide-details
                                @click:append-inner="showLinkedHotel"
                            />
                            <p v-if="address" class="text-grey text-body-2 font-weight-light pt-1">{{ address }}</p>
                        </v-col>
                        <v-col cols="12" :md="2">
                            <v-text-field
                                v-model="refNumber"
                                density="compact"
                                variant="outlined"
                                :label="`${$t('reference_number')}`"
                                hide-details
                            />
                            <div
                                v-if="$conf.public.account.addOfflineServicePNR && type === 'FLIGHT'"
                                class="full-width d-flex"
                            >
                                <v-spacer />
                                <v-btn size="x-small" variant="text" class="mt-1 ms-auto" color="primary">
                                    {{ $t('update_pnr_gds') }}
                                </v-btn>
                            </div>
                        </v-col>
                        <v-col cols="12" :md="3">
                            <v-autocomplete
                                v-model="supplierId"
                                density="compact"
                                :disabled="suppliersLoading || !!serviceToModify"
                                :items="suppliers"
                                :loading="suppliersLoading"
                                item-title="name"
                                item-value="id"
                                hide-no-data
                                :label="`${$t('supplier_id')}*`"
                                persistent-placeholder
                                variant="outlined"
                                bg-color="white"
                                :clearable="true"
                                :rules="[v => !!v || `${$t('supplier_id')} ${$t('validation.required')}`]"
                            />
                            <div v-if="!serviceToModify" class="full-width d-flex flex-row align-center mt-n4">
                                <v-spacer />
                                <span
                                    style="z-index: 1"
                                    class="text-primary cursor-pointer font-weight-bold ms-auto"
                                    @click="newSupplierMode = true"
                                >
                                    {{ $t('add_supplier') }}
                                </span>
                            </div>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" :md="4">
                            <date-picker
                                v-if="type === 'BANK_FEE'"
                                v-model="startDate"
                                outlined
                                dense
                                :label="$t('modify.subtitle_dates')"
                                hide-details
                            />
                            <daterange-m
                                v-else
                                outlined
                                dense
                                disable-min-date
                                :label="$t('modify.subtitle_dates')"
                                :_start="startDate"
                                :_end="endDate"
                                @change-start="startDate = $event"
                                @change-end="endDate = $event"
                            />
                        </v-col>
                        <v-col cols="12" :md="2">
                            <v-select
                                v-model="quantityOfTravelers"
                                variant="outlined"
                                hide-details
                                density="compact"
                                :items="quantityOfPaxItems"
                                :label="$t('quantity_of_pax')"
                            />
                        </v-col>
                    </v-row>
                    <v-row v-if="status === 'PRE-BOOKING'">
                        <v-col :md="4">
                            <!-- eslint-disable vue/no-custom-modifiers-on-v-model -->
                            <vc-date-picker
                                v-model.string="dueToConfirmDate"
                                :masks="{modelValue: 'YYYY-MM-DDTHH:mm'}"
                                :locale="$i18n.locale"
                                :first-day-of-week="$conf.public.firstDayOfWeek || undefined"
                                mode="dateTime"
                                is24hr
                                :popover="{placement: 'bottom', visibility: 'click'}"
                                is-required
                            >
                                <template v-slot="{inputEvents}">
                                    <div v-on="inputEvents">
                                        <v-text-field
                                            :label="$t('due_to_confirm_by')"
                                            readonly
                                            hide-details
                                            bg-color="white"
                                            prepend-inner-icon="mdi-av-timer"
                                            :model-value="$filters.dateTimeFormat(dueToConfirmDate)"
                                            variant="outlined"
                                            density="compact"
                                        />
                                    </div>
                                </template>
                            </vc-date-picker>
                        </v-col>
                    </v-row>
                    <offline-service-product-form
                        v-if="isResetFinished"
                        v-model="product"
                        :product-type="type"
                        :tourists="tourists"
                        @change-start-date="startDate = $event"
                        @change-end-date="endDate = $event"
                    />
                    <v-skeleton-loader v-else class="py-4 pt-8" type="paragraph" />
                    <v-row v-if="isResetFinished">
                        <v-col cols="12">
                            <NewHtmlEditor v-model="description" :label="`${$t('description')}`" />
                        </v-col>
                        <v-col v-if="showATOLSwitcher && type === 'FLIGHT'" cols="12">
                            <div class="text-caption mb-n3">
                                {{ $t('include_atol') }}
                            </div>
                            <v-switch v-model="isATOL" hide-details inset :label="`ATOL`" color="primary" />
                        </v-col>
                        <v-col v-else cols="12">
                            <v-textarea
                                v-model="comment"
                                density="compact"
                                hide-details
                                rows="1"
                                variant="outlined"
                                :label="`${$t('comment')}`"
                            />
                        </v-col>
                    </v-row>
                    <v-skeleton-loader v-else class="py-4" type="paragraph, heading" />
                    <template v-if="isSalesTermsDataLoaded">
                        <v-row>
                            <v-col>
                                <modify-manual-sales-term
                                    :ref="`SUPPLIERSaleTerm`"
                                    v-model="supplierSalesTermValid"
                                    :client-type="clientType"
                                    select-currency
                                    :supplier-price="salesTermSupplier.price"
                                    :client-price="salesTermClient.price"
                                    :disable-cancel-penalty="type === 'BANK_FEE'"
                                    :pricing-rules="pricingRules"
                                    :sales-settings="salesSettings"
                                    :_sales-term="salesTermSupplier"
                                    :quantity="product.quantityOfRooms"
                                    is-offline-service
                                    allow-negative-values
                                    @change-sales-term="salesTermSupplier = $event"
                                    @recalculate-client-price="recalculateClientPrice"
                                    @init-finished="onSalesTermsInitSupplier"
                                />
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col>
                                <modify-manual-sales-term
                                    :ref="`CLIENTSaleTerm`"
                                    v-model="clientSalesTermValid"
                                    :client-type="clientType"
                                    select-currency
                                    :supplier-price="salesTermSupplier.price"
                                    :client-price="salesTermClient.price"
                                    :disable-cancel-penalty="type === 'BANK_FEE'"
                                    :single-currency="$conf.public.account.addOfflineServiceSingleCurrencyClientPrice"
                                    :pricing-rules="pricingRules"
                                    :sales-settings="salesSettings"
                                    :set-contract-currency="
                                        salesSettings &&
                                        salesSettings.performSalesMode === 'CONTRACT_CURRENCY' &&
                                        !serviceToModify
                                    "
                                    :_sales-term="salesTermClient"
                                    :_booking-x-r="bookingXR"
                                    :_service-creation-x-r="serviceCreationXR"
                                    :service-status="status"
                                    is-offline-service
                                    :quantity="product.quantityOfRooms"
                                    allow-negative-values
                                    @change-sales-term="salesTermClient = $event"
                                    @init-finished="onSalesTermsInitClient"
                                />
                            </v-col>
                        </v-row>
                    </template>
                    <div v-else class="d-flex flex-column py-4">
                        <v-skeleton-loader class="my-2" type="paragraph" />
                        <v-skeleton-loader class="my-2" type="paragraph" />
                    </div>
                    <v-row v-if="isShowSeats" dense style="gap: 1rem" class="pt-md-10 pb-4 px-1">
                        <v-checkbox
                            v-model="seatsPrebooked"
                            outlined
                            :label="`${$t('flights.seats_prebooked')}`"
                            hide-details
                        />
                        <v-checkbox
                            v-if="seatsPrebooked"
                            v-model="seatsVisibleForClient"
                            outlined
                            :label="`${$t('show_seat_for')}`"
                            hide-details
                        />
                    </v-row>
                    <offline-service-form-travelers
                        v-if="isResetFinished"
                        ref="travelersForm"
                        v-model="tourists"
                        :quantity-of-travelers="quantityOfTravelers"
                        :status="status"
                        :client-person="clientPerson"
                        :order-travelers="orderTravelers"
                        :default-travelers="corporateClientUsers"
                        :service-travelers="serviceToModify && serviceToModify.travelers"
                        @on-type-change="onTouristTypeChange"
                    >
                        <template v-if="seatsPrebooked && isShowSeats" v-slot="{index}">
                            <v-row v-if="seats?.length" class="pt-2">
                                <v-col cols="12" md="2">
                                    <v-text-field
                                        v-model="findSeat(index).seatNumber"
                                        variant="outlined"
                                        density="compact"
                                        :label="`${$t('flights.seat_number')}`"
                                        hide-details
                                    />
                                </v-col>
                            </v-row>
                        </template>
                    </offline-service-form-travelers>
                    <div v-else class="d-flex flex-column py-4">
                        <v-skeleton-loader class="my-2" type="heading" />
                        <v-skeleton-loader class="my-2" type="heading" />
                    </div>
                </v-form>
                <linked-hotel-modal v-model="showLinkedHotelModal" @save="saveHotelLink($event)" />
            </template>
        </template>
        <template v-slot:btns>
            <template v-if="!newSupplierMode">
                <v-btn class="mx-1" variant="flat" color="primary" :loading="loading" @click="sendRequest">
                    {{ $t('save') }}
                </v-btn>
            </template>
        </template>
    </ModalWrapper>
</template>

<script lang="ts">
    import {Component, Emit, mixins, Watch} from 'vue-facing-decorator'
    import ModifyManualTraveler from '@/components/account/modify/manual/ModifyManualTraveler.vue'
    import ModifyManualStatus from '@/components/account/modify/manual/ModifyManualStatus.vue'
    import DaterangeM from '~src/components/search/forms/daterangeM.src'
    import ModifyManualSalesTerm from '~src/components/account/modify/manual/ModifyManualSalesTerm.vue'
    import CreateSupplierForm from '@/components/account/offlineServices/CreateSupplierForm.vue'
    import OfflineServiceProductForm from '@/components/account/offlineServices/OfflineServiceProductForm.vue'
    import TimeField from '@/components/snippets/forms/TimeField.vue'
    import DatePicker from '@/components/snippets/forms/DatePicker.vue'
    import {FormValidationRulesMixin} from '@/mixins/FormValidationRulesMixin.vue'
    import {OfflineServiceMixin} from '~src/components/account/offlineServices/mixins/OfflineServiceMixin.vue'
    import {OfflineServiceLinkingHotelMixin} from '~src/components/account/offlineServices/mixins/OfflineServiceLinkingHotelMixin.vue'
    import PNRImportDialog from '~/components/account/offlineServices/PNRImportDialog.vue'
    import SkeletonLoader from '~/components/SkeletonLoader.vue'

    @Component({
        components: {
            PNRImportDialog,
            OfflineServiceProductForm,
            CreateSupplierForm,
            ModifyManualSalesTerm,
            ModifyManualStatus,
            ModifyManualTraveler,
            DaterangeM,
            TimeField,
            DatePicker,
            NewHtmlEditor: defineAsyncComponent({
                loader: () => import('@/components/admin/NewHtmlEditor.vue'),
                loadingComponent: SkeletonLoader,
            }),
            OfflineServiceFormTravelers: defineAsyncComponent(
                () => import('~src/components/account/offlineServices/forms/OfflineServiceFormTravelers.vue')
            ),
            LinkedHotelModal: defineAsyncComponent(
                () => import('@/components/account/offlineServices/LinkedHotelModal.vue')
            ),
        },
    })
    export class AddOfflineServiceDialog extends mixins(
        FormValidationRulesMixin,
        OfflineServiceMixin,
        OfflineServiceLinkingHotelMixin
    ) {
        dialog = false
        valid = false
        supplierSalesTermValid = false
        clientSalesTermValid = false
        loading = false
        suppliers = []
        suppliersLoading = false
        isSuppliersLoaded = false
        pricingRules = null
        pricingRulesLoaded = false
        salesSettings = null
        salesSettingsLoaded = false
        destination = null
        corporateClientUsers = null

        @Emit()
        updateLinkedHotel() {
            if (this.product.hotelId) return this.hotelInfo
            if (this.isDeleteLinkedHotel) {
                this.hotelInfo = null
                this.isDeleteLinkedHotel = false
                return null
            }
        }

        @Watch('salesTermSupplier.price.currency')
        onSalesTermSupplierCurrencyChange() {
            this.$refs.CLIENTSaleTerm?.applyExchangeRate()
        }

        @Watch('status')
        async onStatusChange() {
            if (this.status !== 'PRE-BOOKING') {
                this.dueToConfirmDate = null
            } else {
                this.dueToConfirmDate = this.serviceToModify?.dueToConfirmDate?.split(' ').join('T') || null
            }
        }

        @Watch('type')
        async onProductTypeChange() {
            if (!this.serviceToModify && !this.PNRRequestDataInit) {
                this.product = this.type === 'FLIGHT' ? {segments: [{brandedFare: {name: ''}}]} : {}
            }

            if (
                !this.serviceToModify ||
                this.salesTermSupplier?.price?.amount === 0 ||
                this.salesTermClient?.price?.amount === 0
            ) {
                await this.loadPricingRules()
            } else {
                this.pricingRulesLoaded = true
            }
        }

        @Watch('supplierId')
        async onSupplierIdChange() {
            if (
                !this.serviceToModify ||
                this.salesTermSupplier?.price?.amount === 0 ||
                this.salesTermClient?.price?.amount === 0
            ) {
                await this.loadPricingRules()
            } else {
                this.pricingRulesLoaded = true
            }
        }

        @Watch('dialog')
        async onDialogChange() {
            this.isResetFinished = false

            if (this.dialog && !this.isSuppliersLoaded) {
                await this.getSuppliers()
            }
            await this.reset()
            if (this.dialog) {
                await this.loadSalesSettings()
            }

            if (this.destination) {
                if (this.dialog) {
                    const {dateFrom, dateTo} = this.destination
                    this.startDate = dateFrom
                    this.endDate = dateTo
                } else {
                    this.destination = null
                }
            }
            if (this.dialog && this.clientType === 'CORPORATE') {
                await this.loadCorporateClientUsers()
            }

            this.$refs.form?.validate()
        }

        @Watch('tourists.length', {deep: true, immediate: true})
        onTouristsChange() {
            if (this.isTO1) {
                this.seats = this.prepareSeat
            }
        }
        async loadCorporateClientUsers() {
            try {
                const {users} = await this.$api.persons.get({companyId: this.order.clientId, active: true})
                this.corporateClientUsers = users.map(user => ({
                    prefix: user.prefix,
                    firstName: user.firstName || user.firstNameLocalized || '',
                    lastName: user.lastName || user.lastNameLocalized || '',
                    middleName: user.middleName || user.middleNameLocalized || '',
                    dateOfBirth: null,
                    citizenshipId: null,
                    email: user.email,
                    phone: user.contactPhone,
                }))
                // eslint-disable-next-line no-empty
            } catch (e) {}
        }

        async loadSalesSettings() {
            this.salesSettingsLoaded = false

            try {
                this.salesSettings = await this.$api.salesSettings.get({clientCompanyId: this.customerCompanyId})

                // eslint-disable-next-line no-empty
            } catch (e) {}

            this.salesSettingsLoaded = true
        }

        async loadPricingRules() {
            this.pricingRulesLoaded = false

            let type
            switch (this.type) {
                case 'ACCOMMODATION':
                    type = 'accommodation'
                    break
                case 'TRANSFER':
                    type = 'transfer'
                    break
                case 'FLIGHT':
                    type = 'flight'
                    break
                case 'ACTIVITY':
                case 'EXCURSION':
                    type = 'excursion'
                    break
                case 'CAR_RENT':
                    type = 'carRent'
                    break
                case 'INSURANCE':
                    type = 'insurance'
                    break
                case 'TOUR':
                    type = 'tour'
                    break
                case 'VISA':
                    type = 'ownVisa'
                    break
                case 'CRUISE':
                    type = 'ownCruise'
                    break
                case 'OTHER':
                    type = 'extraService'
                    break
                default:
                    break
            }

            if (this.supplierId) {
                try {
                    this.pricingRules = await this.$api.pricingRules.get({
                        clientCompanyId: this.customerCompanyId,
                        productType: type,
                        supplierCode: this.suppliers.find(s => s.id === this.supplierId)?.code,
                    })

                    // eslint-disable-next-line no-empty
                } catch (e) {}
            }
            this.pricingRulesLoaded = true
        }

        async getSuppliers() {
            try {
                this.suppliersLoading = true
                this.suppliers = (await this.$api.suppliers.get({productType: 'extraService'}))?.suppliers

                // eslint-disable-next-line no-empty
            } catch (e) {
            } finally {
                this.suppliersLoading = false
                this.isSuppliersLoaded = true
            }
        }

        saveSupplier(supplier) {
            this.newSupplierMode = false
            if (supplier.companyId) this.supplierId = supplier.companyId
            else this.supplierId = null
            this.getSuppliers()
        }

        onTouristTypeChange() {
            this.$refs.form?.validate()
        }

        recalculateClientPrice() {
            this.$refs.CLIENTSaleTerm?.calculatePriceAmount()
            this.$refs.CLIENTSaleTerm?.calculateMarkup()
            this.$refs.CLIENTSaleTerm?.calculateCommission()
            this.$refs.CLIENTSaleTerm?.calculateCommissionVat()
        }

        async sendRequest() {
            await this.$refs.form.validate()
            if (this.isSaveDisabled) return
            try {
                this.loading = true
                if (this.serviceToModify) {
                    //TODO '|' for processId slashes replace in put requests

                    await this.$api.orderServices.put(
                        this.serviceToModify.processId.replace(/^.{6}/, ''),
                        this.requestBody
                    )
                } else {
                    const {serviceId} = await this.$api.orderServices.post(this.requestBody)

                    if (this.destination) {
                        try {
                            await this.$api.orderDestinations.put(this.order.orderId, {
                                ...this.destination,
                                serviceIds: [...this.destination.serviceIds, serviceId],
                            })
                            this.destination.serviceIds.push(serviceId)
                        } catch (e) {
                            /* empty */
                        }
                    }
                }

                this.$emit('send-request')
                this.$toast.success(this.$t('success_message.saved_successfully'))
                this.dialog = false
                this.updateLinkedHotel()
            } catch (e) {
                if (e.errors?.length) {
                    const firstToken = e.errors[0].token.split(' ').join('_').toLowerCase()

                    if (firstToken === 'max_length_of_description_is_4000') {
                        this.$toast.error(this.$t(`api_error_token.${firstToken}`))
                    } else if (firstToken === 'max_length_of_comment_is_1024') {
                        this.$toast.error(this.$t(`api_error_token.${firstToken}`))
                    } else this.$toast.error(this.$t('error_message.save_error'))
                } else this.$toast.error(this.$t('error_message.save_error'))
            } finally {
                this.loading = false
            }
        }

        showWithDestination(destination) {
            this.destination = destination
            this.dialog = true
        }

        get nameLabel() {
            return `${this.$t('name')}*`
        }

        get isSaveDisabled() {
            return (
                !this.supplierId ||
                !this.valid ||
                !this.supplierSalesTermValid ||
                !this.clientSalesTermValid ||
                this.product.roomType?.length > 256 ||
                !this.isSalesTermsDataLoaded
            )
        }

        get isSalesTermsDataLoaded() {
            return this.pricingRulesLoaded && this.salesSettingsLoaded && this.isResetFinished
        }

        get quantityOfPaxItems() {
            const arr = Array.from(Array(501).keys())
            arr.shift()
            return arr
        }

        get isTO1() {
            return authStore.isTO1
        }

        get isShowSeats() {
            if (!this.isTO1) return false
            return (
                (this.type === 'TRANSFER' && runtimeStore.getCRMOption('asp.option.ownTransfers.show.seats')) ||
                (this.type === 'EXCURSION' && runtimeStore.getCRMOption('asp.option.ownExcursions.show.seats'))
            )
        }

        findSeat(travelerIndex) {
            return this.seats?.find(({travelerRPH}) => travelerRPH == travelerIndex)
        }

        get prepareSeat() {
            if (this.seats?.length) {
                return this.tourists.map((guest, index) => {
                    let existingEntry = this.seats?.find(entry => entry.travelerRPH === index.toString())
                    return existingEntry || {travelerRPH: index.toString(), seatNumber: ''}
                })
            }
            return this.tourists?.map((guest, index) => {
                let existingEntry = this.serviceToModify?.modifyManualSeat?.length
                    ? this.serviceToModify?.modifyManualSeat?.find(entry => entry.travelerRPH === index.toString())
                    : false

                return existingEntry || {travelerRPH: index.toString(), seatNumber: ''}
            })
        }
    }

    export default AddOfflineServiceDialog
</script>

<style scoped lang="scss">
    .modify-link {
        font-size: 14px;
        font-weight: normal;
        display: block;
        text-align: center;
    }
</style>
